import React from 'react';
import { Dropdown, Tag } from 'antd';
import {
  EyeOutlined,
  CopyOutlined,
  StarOutlined,
  DeleteOutlined,
  MoreOutlined,
  MailOutlined,
} from '@ant-design/icons';
import { ViewProps } from '../types.d';
import { TEMPLATE_TABLE_CONSTANTS } from '../constants';
import { getTemplateTypeLabel, getStatusColor } from '../../constants/emailConstants';
import { formatDateAndTime } from '../../../../utils/formatDateTime';
import {
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  MobileCard,
  MobileCardHeader,
  MobileCardTitle,
  MobileCardDetails,
  DetailItem,
  DetailLabel,
  DetailValue,
  MenuButton,
} from '../styles';

const MobileView: React.FC<ViewProps> = ({
  templates,
  templateTypes,
  onEdit,
  onDuplicate,
  onSetDefault,
  onDelete,
  onPreview,
  onTestEmail,
}) => {
  const handleMenuClick = (template: any, action: string) => {
    const handlers: Record<string, () => void> = {
      preview: () => onPreview(template),
      edit: () => onEdit(template),
      duplicate: () => onDuplicate(template),
      setDefault: () => onSetDefault(template),
      testEmail: () => onTestEmail?.(template),
      delete: () => onDelete(template),
    };

    handlers[action]?.();
  };

  const menuItems = (template: any) => [
    template.htmlContent && {
      key: 'preview',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.PREVIEW_HTML,
      icon: <EyeOutlined />,
    },
    template.htmlContent && { type: 'divider' },
    {
      key: 'edit',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.VIEW_EDIT,
      icon: <EyeOutlined />,
    },
    {
      key: 'duplicate',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.DUPLICATE,
      icon: <CopyOutlined />,
    },
    onTestEmail && {
      key: 'testEmail',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.SEND_TEST,
      icon: <MailOutlined />,
    },
    !template.isDefault && {
      key: 'setDefault',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.SET_AS_DEFAULT,
      icon: <StarOutlined />,
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: TEMPLATE_TABLE_CONSTANTS.BUTTONS.DELETE,
      icon: <DeleteOutlined />,
      danger: true,
      disabled: template.isDefault,
    },
  ].filter(Boolean);

  if (templates.length === 0) {
    return (
      <EmptyState>
        <EmptyIcon>
          <MailOutlined />
        </EmptyIcon>
        <EmptyTitle>{TEMPLATE_TABLE_CONSTANTS.MESSAGES.NO_TEMPLATES}</EmptyTitle>
        <EmptyDescription>{TEMPLATE_TABLE_CONSTANTS.MESSAGES.NO_TEMPLATES_DESCRIPTION}</EmptyDescription>
      </EmptyState>
    );
  }

  return (
    <div>
      {templates.map((template) => (
        <MobileCard key={template.id}>
          <MobileCardHeader>
            <MobileCardTitle>
              {template.name}
              {template.htmlContent && (
                <EyeOutlined
                  onClick={() => onPreview(template)}
                  style={{ cursor: 'pointer', opacity: 0.7 }}
                />
              )}
            </MobileCardTitle>
            <Dropdown
              menu={{
                items: menuItems(template).map((item: any) => ({
                  ...item,
                  onClick: () => handleMenuClick(template, item.key),
                })),
              }}
              trigger={['click']}
            >
              <MenuButton icon={<MoreOutlined />} />
            </Dropdown>
          </MobileCardHeader>

          {template.description && (
            <div style={{ marginBottom: 12, fontSize: 13, opacity: 0.8 }}>
              {template.description}
            </div>
          )}

          {template.subject && (
            <div style={{ marginBottom: 12, fontSize: 12, opacity: 0.7 }}>
              {TEMPLATE_TABLE_CONSTANTS.MESSAGES.SUBJECT_PREFIX} {template.subject}
            </div>
          )}

          <MobileCardDetails>
            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.TYPE}</DetailLabel>
              <Tag color="blue">{getTemplateTypeLabel(template.type, templateTypes)}</Tag>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.STATUS}</DetailLabel>
              <Tag color={getStatusColor(template.status)}>
                {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
              </Tag>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.DEFAULT}</DetailLabel>
              {template.isDefault ? (
                <Tag color="green">
                  <StarOutlined style={{ marginRight: 4 }} />
                  Default
                </Tag>
              ) : (
                <DetailValue>-</DetailValue>
              )}
            </DetailItem>

            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.USAGE_COUNT}</DetailLabel>
              <DetailValue>{template.usageCount || 0}</DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.LAST_USED}</DetailLabel>
              <DetailValue>
                {template.lastUsed ? formatDateAndTime(template.lastUsed).formattedDate : TEMPLATE_TABLE_CONSTANTS.MESSAGES.NEVER}
              </DetailValue>
            </DetailItem>

            <DetailItem>
              <DetailLabel>{TEMPLATE_TABLE_CONSTANTS.LABELS.CREATED}</DetailLabel>
              <div>
                <DetailValue>{formatDateAndTime(template.createdAt).formattedDate}</DetailValue>
                {template.createdBy && (
                  <DetailValue style={{ fontSize: 12, opacity: 0.6 }}>
                    {TEMPLATE_TABLE_CONSTANTS.MESSAGES.BY_PREFIX} {template.createdBy}
                  </DetailValue>
                )}
              </div>
            </DetailItem>
          </MobileCardDetails>
        </MobileCard>
      ))}
    </div>
  );
};

export default MobileView;
